import React from 'react'
import ReactDOM from 'react-dom/client'
import { AuthProvider, DevAuthProvider } from '@revalize/auth/frontend'
import { App } from './App'
import { ConfigService } from './services/ConfigService'
import { AppErrorBoundary } from './platform/monitoring/AppErrorBoundary'
import { startMonitoring } from './platform/monitoring/monitoring'
import './index.css'

// Get or create React root
const rootElement = document.getElementById('root')!
const root = ReactDOM.createRoot(rootElement)

/**
 * Initialize app: load runtime config then render
 */
async function initializeApp(): Promise<void> {
  try {
    // Load runtime config from /api/config (always runs — media CDN config is needed in all modes)
    await ConfigService.initialize()
    
    // NOTE: features are no longer awaited here. `features/registry` resolves
    // nav items synchronously at module load and defers every page behind
    // `lazy()`, so first paint no longer waits on 11 feature chunks.

    // Get config for rendering
    const oidcConfig = ConfigService.getOidcConfig()
    const cognitoExtensions = ConfigService.getCognitoExtensions()
    const isDevMode = ConfigService.isDevAuthMode()
    
    // Render app with appropriate auth provider
    root.render(
      <React.StrictMode>
        <AppErrorBoundary>
          {isDevMode ? (
            <DevAuthProvider>
              <App />
            </DevAuthProvider>
          ) : oidcConfig ? (
            <AuthProvider
              {...oidcConfig}
              {...cognitoExtensions}
            >
              <App />
            </AuthProvider>
          ) : (
            <div>Configuration error: OIDC config required</div>
          )}
        </AppErrorBoundary>
      </React.StrictMode>
    )

    // Monitoring loads AFTER the first render so the Sentry SDK (~950 KB of
    // source, incl. Session Replay) stays out of the entry chunk. Deliberately
    // not awaited — startup must not block on observability.
    const monitoringConfig = ConfigService.getMonitoringConfig()
    if (monitoringConfig) {
      void startMonitoring(monitoringConfig)
    }
  } catch (error) {
    // Render error screen on fatal config failure
    root.render(
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontFamily: 'system-ui'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '600px' }}>
          <h1>Configuration Error</h1>
          <p>{error instanceof Error ? error.message : 'Unknown error'}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    )
  }
}

// Show loading screen while config loads (vanilla HTML, not React)
rootElement.innerHTML = `
  <div style="display:flex;align-items:center;justify-content:center;height:100vh">
    <div style="text-align:center">
      <div style="font-size:24px;margin-bottom:16px">⚙️</div>
      <div style="font-size:16px;color:#666">Loading configuration...</div>
    </div>
  </div>
`

// Start initialization
initializeApp()
